import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import debounce from 'lodash.debounce';
import api from 'config/axiosConfig';
import InputField from 'ui/InputField';
import EntityAvatar from 'components/Entities/EntityAvatar';
import AvatarRing from 'ui/AvatarRing';
import { ACTOR_AVATAR_SIZES } from 'util/actorAvatarSizes';
import {
  getActorInitials,
  resolveActorAvatarUrl,
} from 'util/actorIdentity';
import type { Coauthor, CoauthorSuggestion } from "types/coauthor";
import type { Actor } from "types/actor";
import { apiRoutes } from "util/apiRoutes";
import { useUser } from "util/UserContext";
import { useBodyScrollLock } from "ui/useBodyScrollLock";

interface Props {
  onSelect: (suggestion: CoauthorSuggestion) => void;
  onEntityChipClose?: () => void;
  onSearchChange?: () => void;
  placeholder?: string;
  excludeIds?: number[];
  inputType?: 'search' | 'add';
  className?: string;
  autoFocus?: boolean;
  fieldRequired?: boolean;
  validationMessage?: string;
  validationState?: 'none' | 'valid' | 'invalid';
  fieldValue?: string;
  prepopulateData?: CoauthorSuggestion | null;
  /**
   * When false (default), the authenticated user is filtered out of the
   * user suggestion list. A user is already the author of their own content,
   * so offering themselves as a coauthor is always wrong and the backend
   * will reject it. Opt in only for niche flows (none today).
   */
  includeSelf?: boolean;
}

export type { CoauthorSuggestion, Coauthor };


const CoauthorSelect: React.FC<Props> = ({
  onSelect,
  onSearchChange,
  placeholder = "Search users or teams...",
  excludeIds = [],
  inputType = 'search',
  className = "",
  autoFocus = false,
  fieldRequired = false,
  fieldValue = '',
  validationMessage = '',
  validationState = 'none',
  onEntityChipClose,
  prepopulateData = null,
  includeSelf = false,
}) => {
  const { currentUser } = useUser();
  const [searchQuery, setSearchQuery] = useState(fieldValue || '');
  const [searchSuggestions, setSearchSuggestions] = useState<CoauthorSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [coauthorSelected, setCoauthorSelected] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<CoauthorSuggestion | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  /**
   * Map a coauthor suggestion to a canonical `Actor` so the
   * standardized resolver can compose the avatar URL. The picker row
   * is itself a button (clicking selects the suggestion), so we render
   * the avatar via the lower-level `AvatarRing` without a nested link.
   */
  const toActor = (suggestion: CoauthorSuggestion): Actor => ({
    id: typeof suggestion.id === 'number' ? suggestion.id : 0,
    kind: suggestion.type,
    username:
      (suggestion.type === 'team'
        ? suggestion.teamname
        : suggestion.username) ?? suggestion.name ?? '',
    displayName: suggestion.display_name ?? suggestion.name ?? null,
    avatarKey:
      typeof suggestion.avatar_key === 'string' && suggestion.avatar_key.trim().length > 0
        ? suggestion.avatar_key.trim()
        : null,
  });
  useBodyScrollLock(showSuggestions);

  /**
   * User-side exclude list. Teams intentionally stay on the original
   * `excludeIds` list (backed by the server's global id sequence) so a team
   * that shares an id-space row with the current user is not dropped.
   */
  const excludedUserIds = useMemo<number[]>(() => {
    const base = Array.isArray(excludeIds) ? excludeIds : [];
    if (includeSelf) {
      return base;
    }
    const selfId = currentUser?.id;
    if (typeof selfId !== "number" || Number.isNaN(selfId)) {
      return base;
    }
    if (base.includes(selfId)) {
      return base;
    }
    return [...base, selfId];
  }, [excludeIds, includeSelf, currentUser?.id]);

  useEffect(() => {
    if (prepopulateData) {
      setSearchQuery(prepopulateData.name);
      setCoauthorSelected(true);
      setSelectedSuggestion(prepopulateData);
    } else {
      setCoauthorSelected(false);
      setSelectedSuggestion(null);
      setSearchQuery('');
    }
  }, [prepopulateData]);

  const performSearch = async (value: string) => {
    try {
      let suggestions: CoauthorSuggestion[] = [];

      if (!value) {
        // If no search value, fetch user's teams
        const teamsResponse = await api.get(apiRoutes.team.member);
        suggestions = teamsResponse.data.map((team: any) => ({
          id: team.id,
          type: 'team' as const,
          name: team.teamname,
          display_name: team.display_name,
          teamname: team.teamname,
          avatar_key: team.properties_public?.avatar_key,
          properties_public: team.properties_public
        }));
      } else {
        // Search for both users and teams
        const [usersResponse, teamsResponse] = await Promise.all([
          api.get(apiRoutes.user.search, { params: { q: value } }),
          api.get(apiRoutes.team.root, { params: { q: value } })
        ]);

        const userSuggestions = usersResponse.data
          .filter((user: any) => !excludedUserIds.includes(user.id))
          .map((user: any) => ({
            id: user.id,
            type: 'user' as const,
            name: user.username,
            display_name: user.display_name,
            username: user.username,
            avatar_key: user.properties_public?.avatar_key,
            properties_public: user.properties_public
          }));

        const teamSuggestions = teamsResponse.data
          .filter((team: any) => !excludeIds.includes(team.id))
          .map((team: any) => ({
            id: team.id,
            type: 'team' as const,
            name: team.teamname,
            display_name: team.display_name,
            teamname: team.teamname,
            avatar_key: team.properties_public?.avatar_key,
            properties_public: team.properties_public
          }));

        suggestions = [...userSuggestions, ...teamSuggestions].slice(0, 15);
      }

      setSearchSuggestions(suggestions);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching search results:', error);
    }
  };

  // Debounce the search to prevent excessive API calls.
  // Include the exclude lists so the closure always uses the current filters;
  // otherwise adding coauthors wouldn't hide newly-selected rows from follow-up searches.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => performSearch(value), 360),
    [excludedUserIds, excludeIds]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
    setShowSuggestions(true);
    debouncedSearch(value);
    if (onSearchChange) {
      onSearchChange();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchSuggestions([]);
    setShowSuggestions(false);
    searchInputRef.current?.blur();
  };

  const handleSelectSuggestion = (suggestion: CoauthorSuggestion) => {
    onSelect(suggestion);
    setSelectedSuggestion(suggestion);
    setShowSuggestions(false);
    setSearchQuery('');
    setCoauthorSelected(false);
  };

  const handleEntityChipClose = () => {
    setSelectedSuggestion(null);
    setCoauthorSelected(false);
    setSearchQuery('');
    fieldValue = '';
    if (onEntityChipClose) {
      onEntityChipClose();
    }
    document.getElementById(`coauthor-add-${placeholder}`)?.focus();
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        {inputType === 'search' ? (
          <>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              ref={searchInputRef}
              placeholder={placeholder}
              autoFocus={autoFocus}
              className="eoe-input w-full bg-gray-200 dark:bg-eoe-dark-input rounded-full px-2 py-2 pr-8"
            />
            {searchQuery.length > 0 && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </>
        ) : (
          <>
            {coauthorSelected ? (
              <span className={`input-container relative flex-1`}>
                <div
                  className={`
                    w-full min-h-[60px] px-3 pt-6 pb-1
                    eoe-input bg-gray-200 dark:bg-eoe-dark-input
                    rounded-2xl
                    text-gray-800 dark:text-eoe-dark-text
                    transition-colors
                    resize-none
                  `}
                >
                  <span 
                    className={`
                      absolute left-3
                      top-2 text-xs
                      transition-all
                      text-eoe-text-meta dark:text-eoe-dark-text-meta
                    `}
                  >
                    {placeholder}
                    {fieldRequired && <span className="text-warning-red dark:text-dark-warning-red ml-[0.5px]">*</span>}
                  </span>
                  <EntityAvatar
                    entity={selectedSuggestion as any}
                    entityType={selectedSuggestion?.type || 'user'}
                    entityChip={true}
                    showCloseChip={true}
                    showLink={true}
                    onEntityChipClose={handleEntityChipClose}
                  />
                </div>
              </span>
            ) : (
              <InputField
                id={`coauthor-add-${placeholder}`}
                type="text"
                name={`coauthor-add-${placeholder}`}
                labelText={placeholder}
                fieldRequired={fieldRequired}
                value={searchQuery || fieldValue}
                onChange={handleSearchChange}
                onFocus={() => performSearch('')}
                autoComplete="off"
                ref={searchInputRef}
                autoFocus={prepopulateData ? true : autoFocus}
                validationState={validationState}
                error={validationMessage}
              />
            )}
          </>
        )}
      </div>

      {showSuggestions && searchSuggestions.length > 0 && (
        <div
          className={`search-suggestions-container flex flex-col bg-transparent 
            ${inputType === 'search' ?
              'fixed top-[56px] left-[1px] right-[1px] z-50 max-h-[calc(100dvh-72px)] overflow-hidden md:w-[580px] md:mx-auto md:left-0 md:right-0' : 
              'relative bottom-2 top-1 max-h-[200px] overflow-y-auto'
            }`
          }
        >
          <ul
            className={`bg-eoe-bg dark:bg-eoe-dark-bg border border-eoe-border-main dark:border-eoe-dark-border-main border-b-0 shadow-lg
              ${inputType === 'search' ? 
                'flex-shrink overflow-y-auto max-h-[calc(100dvh-72px)]' :
                'flex-1 overflow-y-auto'
              }
            `}
          >
            {searchSuggestions.map((suggestion, index) => (
              <li
                key={index}
                className="
                  group
                  px-3 py-2
                  hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon
                  cursor-pointer
                  text-eoe-text dark:text-eoe-dark-text
                  flex justify-between items-center
                "
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                <div className="flex items-center space-x-2">
                  {(() => {
                    const actor = toActor(suggestion);
                    return (
                      <AvatarRing
                        size={ACTOR_AVATAR_SIZES.message}
                        ringState="none"
                        imageUrl={resolveActorAvatarUrl(actor)}
                        imageAlt={suggestion.display_name || suggestion.name}
                        initials={getActorInitials(actor)}
                      />
                    );
                  })()}
                  <div className="flex flex-col">
                    <span>
                      {suggestion.username || suggestion.teamname}
                    </span>
                    <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {suggestion.display_name || suggestion.name}
                    </span>
                  </div>
                </div>
                <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${
                  suggestion.type === 'team' ? 'bg-eoe-entity-team' : 'bg-eoe-entity-user'
                } text-white`}>
                  {suggestion.type === 'team' ? 'Partea' : 'User'}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex-shrink-0 border-none">
            <button 
              className="w-full btn-secondary py-[5px] px-2 rounded-tr-none rounded-tl-none" 
              onClick={() => setShowSuggestions(false)}
            >
              <span className="text-eoe-dark-text">Close search</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoauthorSelect; 